/**
 * Builds day/city cards for TripDetail — mirrors iOS CityVisitCard list ordering.
 */

import type { ItineraryItemType, Trip, TripCityVisitMeta } from '../types/client';
import { isAtSeaLocation, parsePortTimeParts } from '../services/cruiseItinerariesApi';
import { addDaysYmd, formatHm, formatYmd, hmFromUnknown, utcTodayYmd, ymdFromUnknown } from './wallClock';
import { shouldShowFlight } from './dayPlanningIconsCruiseRules';

export interface TripDayCardModel {
  cityId: string;
  cityName: string;
  arrivalDate: string | null;
  departureDate: string | null;
  portArrivalHm: string | null;
  portDepartureHm: string | null;
  isAtSea: boolean;
  isCruisePort: boolean;
  cruisePortStatus: TripCityVisitMeta['cruisePortStatus'];
  itemTypes: ItineraryItemType[];
  /** Cruise: show airplane on first embark-city / last debark-city day (#140). */
  showFlightIcon: boolean;
  countryISO2: string | null;
  countryName: string | null;
  currencyCode: string | null;
  timeZoneIdentifier: string | null;
  dockNumber: string | null;
}

export function parseCityVisitMetaFromDevice(body: Record<string, unknown>): TripCityVisitMeta {
  const arrivalDate = ymdFromUnknown(body.arrivalDate);
  const departureDate = ymdFromUnknown(body.departureDate) ?? arrivalDate;
  let portArrivalHm =
    hmFromUnknown(body.portArrivalHm) ??
    hmFromUnknown(body.portArrivalTime) ??
    hmFromUnknown(body.arrivalDate);
  let portDepartureHm =
    hmFromUnknown(body.portDepartureHm) ??
    hmFromUnknown(body.portDepartureTime) ??
    hmFromUnknown(body.departureDate);
  if (!portArrivalHm && !portDepartureHm && typeof body.time === 'string') {
    const parts = parsePortTimeParts(body.time);
    portArrivalHm = parts.arrivalHm;
    portDepartureHm = parts.departureHm;
  }
  const statusRaw = body.cruisePortStatus != null ? String(body.cruisePortStatus) : '';
  const statusNorm = statusRaw.trim().toLowerCase();
  // prePost must match camelCase (iOS raw value); lowercasing alone yields "prepost".
  const cruisePortStatus: TripCityVisitMeta['cruisePortStatus'] =
    statusNorm === 'docked' ||
    statusNorm === 'tendered' ||
    statusNorm === 'cruising' ||
    statusNorm === 'unknown'
      ? statusNorm
      : statusNorm === 'prepost' || statusRaw === 'prePost' || statusRaw === 'PrePost'
        ? 'prePost'
        : null;

  const aiJobId =
    body.aiJobId != null && String(body.aiJobId).trim()
      ? String(body.aiJobId).trim()
      : null;
  const aiJobStartedAt =
    body.aiJobStartedAt != null && String(body.aiJobStartedAt).trim()
      ? String(body.aiJobStartedAt)
      : null;
  const aiStatus =
    body.aiStatus != null && String(body.aiStatus).trim()
      ? String(body.aiStatus).trim()
      : null;
  const aiUpdatedAt =
    body.aiUpdatedAt != null && String(body.aiUpdatedAt).trim()
      ? String(body.aiUpdatedAt)
      : null;

  const blobs =
    body.largeCityBlobs && typeof body.largeCityBlobs === 'object'
      ? (body.largeCityBlobs as Record<string, unknown>)
      : null;
  const aiGeneratedPlanDataFileIdRaw =
    body.aiGeneratedPlanDataFileId ?? blobs?.aiGeneratedPlanDataFileId;
  const aiGeneratedPlanDataFileId =
    aiGeneratedPlanDataFileIdRaw != null && String(aiGeneratedPlanDataFileIdRaw).trim()
      ? String(aiGeneratedPlanDataFileIdRaw).trim()
      : null;

  return {
    arrivalDate,
    departureDate,
    portArrivalHm,
    portDepartureHm,
    cruisePortStatus,
    dockNumber:
      body.dockNumber != null && String(body.dockNumber).trim()
        ? String(body.dockNumber).trim()
        : null,
    isEmbarkation: Boolean(body.isEmbarkation),
    isDebarkation: Boolean(body.isDebarkation),
    countryISO2:
      body.countryISO2 != null && String(body.countryISO2).trim()
        ? String(body.countryISO2).trim().toUpperCase()
        : null,
    countryName:
      body.countryName != null && String(body.countryName).trim()
        ? String(body.countryName).trim()
        : null,
    currencyCode:
      body.currencyCode != null && String(body.currencyCode).trim()
        ? String(body.currencyCode).trim().toUpperCase()
        : null,
    timeZoneIdentifier:
      body.timeZoneIdentifier != null && String(body.timeZoneIdentifier).trim()
        ? String(body.timeZoneIdentifier).trim()
        : null,
    aiJobId,
    aiJobStartedAt,
    aiStatus,
    aiUpdatedAt,
    aiGeneratedPlanDataFileId,
  };
}

function addDaysIso(iso: string, days: number): string {
  return addDaysYmd(iso, days);
}

function daysBetweenInclusive(start: string, end: string): string[] {
  const result: string[] = [];
  let cur = start;
  let guard = 0;
  while (cur <= end && guard < 400) {
    result.push(cur);
    cur = addDaysIso(cur, 1);
    guard += 1;
  }
  return result;
}

/** Inclusive YYYY-MM-DD days for a city visit stay (iOS itineraryDayRange). */
export function enumerateStayDays(
  arrivalDate: string | null | undefined,
  departureDate: string | null | undefined
): string[] {
  const start = ymdFromUnknown(arrivalDate) ?? null;
  if (!start) return [];
  const endRaw = ymdFromUnknown(departureDate) ?? start;
  const end = endRaw >= start ? endRaw : start;
  return daysBetweenInclusive(start, end);
}

/** Plan icons for one city visit day — date-scoped when `date` is set. */
export function itemTypesForCityDay(
  trip: Trip,
  cityId: string,
  date: string | null
): ItineraryItemType[] {
  const days = trip.itineraryDays ?? [];
  const types = new Set<ItineraryItemType>();
  for (const day of days) {
    if (day.cityId && day.cityId !== cityId) continue;
    if (date && day.date !== date) continue;
    for (const item of day.items ?? []) {
      types.add(item.type);
    }
  }
  return Array.from(types);
}

/** True when `date` falls on a civil day of the visit stay. */
export function visitContainsDate(
  card: Pick<TripDayCardModel, 'arrivalDate' | 'departureDate'>,
  date: string
): boolean {
  const ymd = ymdFromUnknown(date);
  if (!ymd) return false;
  return enumerateStayDays(card.arrivalDate, card.departureDate).includes(ymd);
}

/** Ordered city visit cards for TripDetail — one card per cityId (iOS CityVisit). */
export function buildTripDayCards(trip: Trip): TripDayCardModel[] {
  const cityIds = trip.cityIds ?? [];
  if (cityIds.length === 0) return [];

  const isCruise = trip.tripType === 'cruise';
  const metaMap = trip.cityVisitMeta ?? {};
  const dateSpan =
    trip.startDate && trip.endDate ? daysBetweenInclusive(trip.startDate, trip.endDate) : [];

  return cityIds.map((cityId, index) => {
    const normalizedId = cityId.trim();
    const cityName =
      trip.cityNames?.[normalizedId]?.trim() ||
      Object.entries(trip.cityNames ?? {}).find(
        ([id, name]) => id.toLowerCase() === normalizedId.toLowerCase() && name.trim()
      )?.[1]?.trim() ||
      '';
    const displayName =
      cityName && !looksLikeUuid(cityName) ? cityName : 'Unknown city';
    const meta =
      metaMap[normalizedId] ??
      Object.entries(metaMap).find(([id]) => id.toLowerCase() === normalizedId.toLowerCase())?.[1];
    let arrivalDate = meta?.arrivalDate ?? null;
    let departureDate = meta?.departureDate ?? arrivalDate;

    // Fallback: assign sequential dates when count matches trip length (typical cruise import)
    if (!arrivalDate && dateSpan.length === cityIds.length) {
      arrivalDate = dateSpan[index] ?? null;
      departureDate = arrivalDate;
    } else if (!arrivalDate && dateSpan.length > 0 && index < dateSpan.length) {
      arrivalDate = dateSpan[index] ?? null;
      departureDate = arrivalDate;
    }

    const atSea = isAtSeaLocation(displayName);
    return {
      cityId: normalizedId,
      cityName: displayName,
      arrivalDate,
      departureDate,
      portArrivalHm: meta?.portArrivalHm ?? null,
      portDepartureHm: meta?.portDepartureHm ?? null,
      isAtSea: atSea,
      isCruisePort: isCruise && !atSea,
      cruisePortStatus: meta?.cruisePortStatus ?? (isCruise && !atSea ? 'docked' : null),
      itemTypes: itemTypesForCityDay(trip, normalizedId, null),
      showFlightIcon: isCruise && !atSea && shouldShowFlight(trip, normalizedId),
      countryISO2: meta?.countryISO2 ?? null,
      countryName: meta?.countryName ?? null,
      currencyCode: meta?.currencyCode ?? null,
      timeZoneIdentifier: meta?.timeZoneIdentifier ?? null,
      dockNumber: meta?.dockNumber ?? null,
    };
  });
}

function looksLikeUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value.trim());
}

export function formatTripHeaderDate(iso: string | null): string | null {
  if (!iso) return null;
  const ymd = ymdFromUnknown(iso);
  if (!ymd) return iso;
  return formatYmd(ymd, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function formatCardWeekdayDate(iso: string | null): string | null {
  if (!iso) return null;
  const ymd = ymdFromUnknown(iso);
  if (!ymd) return iso;
  return formatYmd(ymd, { weekday: 'short', month: 'short', day: 'numeric' });
}

export function formatPortClockHm(hm: string): string {
  return formatHm(hm);
}

/** Secondary time line under the date — mirrors iOS WeatherCardRow.secondaryDateLine */
export function portTimeLineForCard(card: TripDayCardModel): string | null {
  return portTimeLineForStayDay({
    dayYmd: card.arrivalDate,
    arrivalDate: card.arrivalDate,
    departureDate: card.departureDate,
    portArrivalHm: card.portArrivalHm,
    portDepartureHm: card.portDepartureHm,
  });
}

/**
 * Port / stay time line for one civil day inside a visit — mirrors iOS CityVisitDayTimeLine.
 * First day of a multi-day stay shows arrival; last shows departure; middle days omit.
 */
export function portTimeLineForStayDay(params: {
  dayYmd: string | null;
  arrivalDate: string | null;
  departureDate: string | null;
  portArrivalHm: string | null;
  portDepartureHm: string | null;
}): string | null {
  const day = ymdFromUnknown(params.dayYmd);
  if (!day) return null;

  const stayDays = enumerateStayDays(params.arrivalDate, params.departureDate);
  if (stayDays.length === 0 || !stayDays.includes(day)) return null;

  const firstDay = stayDays[0];
  const lastDay = stayDays[stayDays.length - 1];
  const multiDay = firstDay !== lastDay;

  const arr = multiDay
    ? day === firstDay
      ? params.portArrivalHm
      : null
    : params.portArrivalHm;
  const dep = multiDay
    ? day === lastDay
      ? params.portDepartureHm
      : null
    : params.portDepartureHm;

  if (!arr && !dep) return null;

  const isFullDayDefault =
    arr === '00:00' && (dep === '23:59' || dep === '23:59:00');
  if (isFullDayDefault) return null;

  if (arr && dep) {
    return `${formatPortClockHm(arr)} - ${formatPortClockHm(dep)}`;
  }
  if (arr) return `Arr: ${formatPortClockHm(arr)}`;
  if (dep) return `Dep: ${formatPortClockHm(dep)}`;
  return null;
}

export function pendingOptionCount(trip: Trip): number {
  const options = trip.cityOptions ?? {};
  let count = 0;
  for (const list of Object.values(options)) {
    for (const opt of list) {
      if (!opt.completed) count += 1;
    }
  }
  return count;
}

/** Pending trip-level checklist tasks — mirrors iOS Trip.pendingTaskCount */
export function pendingTaskCount(trip: Trip): number {
  return (trip.taskItems ?? []).filter((t) => !t.isDone).length;
}

export function defaultShowPlanIcons(trip: Trip, now = new Date()): boolean {
  if (!trip.startDate) return true;
  const start = ymdFromUnknown(trip.startDate);
  if (!start) return true;
  return utcTodayYmd(now) < start;
}
